import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { uniqBy } from 'lodash';
import * as Yup from 'yup';

import CheckboxFormik from '~/components/CheckboxFormik';
import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Tooltip from '~/components/core/Atomic/Tooltip';
import Typography from '~/components/core/Atomic/Typography';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import fnolConfigurationCopies from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/copies.json';
import ConfiguredFieldsSection from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/Sections/ConfiguredFieldsSection';
import { configuredFieldSchema } from '~/components/SystemConfiguration/FnolConfiguration/fnolConfigurationValidationSchema';

import { isProductionEnv, reportAxiosError } from '../../../../../../Utils';
import { DownloadIcon } from '../../../../../icons';
import LoadingIndicator from '../../../../../LoadingIndicator';
import { TextFieldFormik } from '../../../../../TextFieldFormik';
import useDataFetcher from '../../../../../useDataFetcher';
import { useSysconfig } from '../../../../SystemConfigurationScreen';

import PolicyManagementInterface from './MockPolicyServer/components/PolicyManagementInterface';
import { useMockPolicyServer } from './MockPolicyServer/hooks/useMockPolicyServer';
import PolicyAPIDataForm from './PolicyAPIDataForm';
import PolicyModeToggleDialog from './PolicyModeToggleDialog';

import { useStyles } from '../../../../../../assets/styles';
import colors from '../../../../../../assets/colors.module.scss';

// Only the v2 search has a choice of method; the v2 retrieval is a GET permanently, because its
// input is a single opaque token. The control defaults to POST for a configuration that has no
// stored value, so anything saved from here on is explicit -- the backend reads an *absent* key as
// GET, which is what the three v2 integrations that predate the key can answer.
const SEARCH_HTTP_METHODS = ['POST', 'GET'];
const DEFAULT_SEARCH_HTTP_METHOD = 'POST';

// The five types that make sense as a policy-search input. The carrier spec builder also maps
// `monetary` and `boolean`, because a configuration saved before this restriction may already hold
// one; they are left off here so a *new* search field cannot be either. (`boolean` would be left
// off in any case -- `ConfigurationFieldType.filterBooleanForNewFields` never offers it for a new
// field -- so naming it here is the honest list rather than an extra restriction.) Derived as the
// complement over the shared type list rather than spelled out as a denylist, so a type added to
// `field_types` later is not silently offered here -- and so the three `yes_no*` aliases, which all
// store `yes_no_na_unknown`, are covered without naming each one.
const SEARCHABLE_FIELD_TYPES = ['string', 'date', 'select', 'multiselect', 'number'];
const NON_SEARCHABLE_FIELD_TYPES = fnolConfigurationCopies.field_types
  .map(({ id }) => id)
  .filter((id) => !SEARCHABLE_FIELD_TYPES.includes(id));

// A type a saved field already holds must stay in the offered set, whatever the restriction above
// says. `ConfigurationFieldType` keeps the Type select *enabled* for an existing `yes_no*` field
// and filters its options to the four bool4 aliases, so excluding all four leaves the operator an
// open dropdown with zero options and a value outside it; for every other excluded type the select
// is disabled but still renders its value from the option list, so excluding it blanks the row.
// Those legacy fields are reachable -- the restriction is new, and the write path never validated
// against it -- which is why the carrier spec builder maps their types too.
const getExcludedFieldTypes = (configuredFields) => {
  const storedTypes = new Set((configuredFields || []).map((field) => field?.type));
  return NON_SEARCHABLE_FIELD_TYPES.filter((id) => !storedTypes.has(id));
};

const PolicyAPIConfig = () => {
  const { classes } = useStyles();
  const { organization, organizationOperationalDetails, reloadOperationalDetails } = useSysconfig();

  const [policyAPIDataFormWizardOpen, setPolicyAPIDataFormOpen] = useState(false);
  const [policyAPIDataFormEditWizardOpen, setPolicyAPIDataFormEditOpen] = useState(false);

  // State for mock integration
  const [isEnabling, setIsEnabling] = useState(false);

  // Mock policy server hook
  const {
    status: mockServerStatus,
    getStatus: getMockServerStatus,
    enable: enableMockServer,
    disable: disableMockServer,
    loading: mockServerLoading,
  } = useMockPolicyServer();

  // Combined loading state for UI
  const isProcessing = isEnabling || mockServerLoading;

  // State for mode toggle dialog
  const [toggleDialog, setToggleDialog] = useState({
    open: false,
    isTogglingToDemo: false,
  });

  const urlPolicyAPIConfig = `/sysconfig/api/v1/organizations/${organization.id}/policy_api_config`;

  const {
    isLoading,
    isError,
    data: policyAPIConfig,
    reloadData: reloadPolicyAPIConfig,
  } = useDataFetcher(urlPolicyAPIConfig);

  // Mock integration detection using backend status
  const isMockIntegrationEnabled = mockServerStatus?.enabled || false;

  // Load mock server status on mount (for non-production environments only)
  const shouldLoadMockStatus = !isProductionEnv();
  useEffect(() => {
    if (shouldLoadMockStatus) {
      getMockServerStatus().catch(() => {
        // Silently handle error - status will remain null/false
      });
    }
  }, [shouldLoadMockStatus, getMockServerStatus]);

  // Updated logic to match expected behavior (non-production environments only):
  // 1. If policy api + mock not enabled - show two buttons
  // 2. If mock enabled - show mock component
  // 3. If policy api enabled (but mock disabled) - show "switch" button
  const showEnablePolicyAPIButton = !organizationOperationalDetails.isPolicyAPIEnabled;
  const showEnableMockButton =
    !isProductionEnv() && !organizationOperationalDetails.isPolicyAPIEnabled && !isMockIntegrationEnabled;
  const showMockInterface = !isProductionEnv() && isMockIntegrationEnabled;
  const showRegularInterface = organizationOperationalDetails.isPolicyAPIEnabled && !showMockInterface;

  // Handler functions for button actions
  const handleEnablePolicyAPI = async () => {
    setIsEnabling(true);
    try {
      await axios.post(`/sysconfig/api/v1/organizations/${organization.id}/policy_api_config/enable`);
      await reloadPolicyAPIConfig();
      await reloadOperationalDetails();
    } catch (error) {
      reportAxiosError(error);
    } finally {
      setIsEnabling(false);
    }
  };

  const handleEnableMockIntegration = async () => {
    setIsEnabling(true);
    try {
      // Enable mock policy server using hook
      await enableMockServer();
      await reloadPolicyAPIConfig();
      await reloadOperationalDetails();
    } catch (error) {
      reportAxiosError(error);
    } finally {
      setIsEnabling(false);
    }
  };

  // New handlers for mode toggle dialog
  const handleOpenToggleDialog = (isTogglingToDemo) => {
    setToggleDialog({
      open: true,
      isTogglingToDemo,
    });
  };

  const handleCloseToggleDialog = () => {
    setToggleDialog({
      open: false,
      isTogglingToDemo: false,
    });
  };

  const handleConfirmToggle = async () => {
    setIsEnabling(true);
    try {
      if (toggleDialog.isTogglingToDemo) {
        // Switch to demo mode using hook
        await enableMockServer();
      } else {
        // Switch to production mode using hook
        await disableMockServer();
      }

      await reloadPolicyAPIConfig();
      await reloadOperationalDetails();
      handleCloseToggleDialog();
    } catch (error) {
      reportAxiosError(error);
    } finally {
      setIsEnabling(false);
    }
  };

  // Memoized because `excludeTypes` was a module constant before this: a fresh array on every
  // render would re-render the whole configured-fields section for nothing.
  const excludedFieldTypes = useMemo(
    () => getExcludedFieldTypes(policyAPIConfig?.configured_fields),
    [policyAPIConfig?.configured_fields]
  );

  if (isLoading || isError) {
    return <LoadingIndicator isError={isError} />;
  }

  const requireSubOrgReason = organization?.organization_type === 'demo' ? 'Required for demo organizations' : '';

  const getEntityInitialValues = {
    base_url: policyAPIConfig?.base_url || '',
    auth_token: policyAPIConfig?.auth_token || '',
    policy_search_component_description: policyAPIConfig?.policy_search_component_description || '',
    enabled: policyAPIConfig?.enabled || false,
    configured_fields: policyAPIConfig?.configured_fields || [],
    version: policyAPIConfig?.version || 1,
    retrieval_url: policyAPIConfig?.retrieval_url || '',
    should_require_sub_org: policyAPIConfig?.should_require_sub_org || !!requireSubOrgReason,
    search_http_method: policyAPIConfig?.search_http_method || DEFAULT_SEARCH_HTTP_METHOD,
  };

  const getValidationSchema = () => ({
    base_url: Yup.string().required('Required'),
    retrieval_url: policyAPIConfig?.version === 2 ? Yup.string().required('Required') : Yup.string().nullable(),
    auth_token: Yup.string().required('Required'),
    policy_search_component_description: Yup.string().required('Required'),
    enabled: Yup.boolean(),
    should_require_sub_org: Yup.boolean(),
    search_http_method: Yup.string().oneOf(SEARCH_HTTP_METHODS).required('Required'),
    configured_fields: Yup.array()
      .of(configuredFieldSchema(policyAPIConfig?.configured_fields || []))
      .test(
        'configured-fields-unique-ids',
        'Additional Fields keys must be unique',
        (value) => uniqBy(value, 'id')?.length === value?.length
      ),
  });

  const getPayload = (values) => {
    return {
      base_url: values.base_url,
      auth_token: values.auth_token,
      policy_search_component_description: values.policy_search_component_description,
      enabled: values.enabled,
      configured_fields: values?.configured_fields || [],
      version: values.version,
      retrieval_url: values.retrieval_url,
      should_require_sub_org: values.should_require_sub_org,
      // `update_policy_api_configuration` replaces the whole stored object, so a key this payload
      // omits is wiped by the next save from this screen -- how the `locale` key was lost once
      // already. PolicyAPIConfig.test.tsx pins this one against that.
      //
      // Sent only for v2, which is the only version that reads it. Writing it on a v1 save would
      // put a misleading key onto a configuration that never opted into v2 -- exactly what the
      // backfill migration refuses to do -- and the wipe on a v2 -> v1 demotion is correct for the
      // same reason: the key describes a v2 request that organization no longer makes.
      ...(values.version === 2 ? { search_http_method: values.search_http_method } : {}),
    };
  };

  const handleSave = async (values) => {
    try {
      const payload = getPayload(values);
      await axios.post(urlPolicyAPIConfig, payload);
      await reloadOperationalDetails();
      await reloadPolicyAPIConfig();
      setPolicyAPIDataFormOpen(false);
    } catch (error) {
      reportAxiosError(error);
    }
  };

  const handleEditSave = async (values) => {
    try {
      const payload = getPayload(values);
      await axios.put(urlPolicyAPIConfig, payload);
      await reloadOperationalDetails();
      await reloadPolicyAPIConfig();
      setPolicyAPIDataFormEditOpen(false);
    } catch (error) {
      reportAxiosError(error);
    }
  };

  const getEditInputs = (isWizardOpen, firstTitle, secondTitle, thirdTitle, switchTitle, values) => (
    <>
      <Grid item xs={12}>
        <Typography display="block" variant="subtitle2" style={{ fontWeight: 700, marginBottom: '10px' }}>
          {switchTitle}
        </Typography>
      </Grid>
      <Grid item xs={isWizardOpen ? 8 : 4}>
        <SwitchFormik id="enabled" label="" disabled={!isWizardOpen} withShowOnlyText />
      </Grid>

      <Grid item xs={12}>
        <Typography display="block" variant="subtitle2" style={{ fontWeight: 700, marginBottom: '10px' }}>
          {firstTitle}
        </Typography>
      </Grid>
      <Grid item xs={isWizardOpen ? 12 : 6}>
        <TextFieldFormik
          id="base_url"
          placeholder="Fill in the base URL"
          className={classes.textField}
          fullWidth
          disabled={!isWizardOpen}
        />
      </Grid>
      <Grid item xs={isWizardOpen ? 12 : 6}>
        <TextFieldFormik
          id="retrieval_url"
          placeholder="Fill in the retrieval policy URL"
          className={classes.textField}
          fullWidth
          disabled={!isWizardOpen || values?.version !== 2}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography display="block" variant="subtitle2" style={{ fontWeight: 700, marginBottom: '10px' }}>
          {secondTitle}
        </Typography>
      </Grid>
      <Grid item xs={isWizardOpen ? 8 : 4}>
        <TextFieldFormik
          id="auth_token"
          placeholder="Fill in the auth token"
          className={classes.textField}
          fullWidth
          disabled={!isWizardOpen}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography display="block" variant="subtitle2" style={{ fontWeight: 700, marginBottom: '10px' }}>
          {thirdTitle}
        </Typography>
      </Grid>
      <Grid item xs={isWizardOpen ? 8 : 4}>
        <TextFieldFormik
          id="policy_search_component_description"
          placeholder="Fill in the policy search component description"
          className={classes.textField}
          fullWidth
          disabled={!isWizardOpen}
        />
      </Grid>
      <Grid item xs={isWizardOpen ? 8 : 12}>
        <TextFieldFormik
          id="version"
          label="Version"
          className={classes.textField}
          fullWidth
          disabled={!isWizardOpen}
          select
          showOnly={!isWizardOpen}
        >
          <MenuItem key={1} value={1}>
            1
          </MenuItem>
          <MenuItem key={2} value={2}>
            2
          </MenuItem>
        </TextFieldFormik>
      </Grid>

      {values?.version === 2 && (
        <Grid item xs={isWizardOpen ? 8 : 12}>
          <TextFieldFormik
            id="search_http_method"
            label="Search Request Method"
            className={classes.textField}
            fullWidth
            disabled={!isWizardOpen}
            select
            showOnly={!isWizardOpen}
          >
            {SEARCH_HTTP_METHODS.map((method) => (
              <MenuItem key={method} value={method}>
                {method}
              </MenuItem>
            ))}
          </TextFieldFormik>
        </Grid>
      )}

      {values?.version === 1 && organization?.sub_organizations_enabled && (
        <Grid item xs={isWizardOpen ? 8 : 4}>
          <Tooltip title={requireSubOrgReason} placement="top">
            <span>
              <CheckboxFormik
                id="should_require_sub_org"
                label="Sub Organization Required"
                disabled={!isWizardOpen || !!requireSubOrgReason}
              />
            </span>
          </Tooltip>
        </Grid>
      )}

      <Grid item xs={12}>
        <ConfiguredFieldsSection
          fieldId="configured_fields"
          subtitle="Policy API Search field to pass to your'e policy API server"
          disabled={!isWizardOpen || values?.version !== 2}
          excludeTypes={excludedFieldTypes}
          disableCategories
          disabledWithoutPadding
          disabledWithoutChevron
          withoutRequiredForClaimClosing
          requiredLabel="Required"
        />
      </Grid>
    </>
  );

  return (
    <Grid container spacing={0} direction="column" style={{ paddingTop: '6px' }}>
      {/* Policy API Mode Status Display - Always on top for non-production environments */}
      {!isProductionEnv() && (organizationOperationalDetails.isPolicyAPIEnabled || isMockIntegrationEnabled) && (
        <Grid item style={{ paddingLeft: '32px', paddingRight: '32px', marginBottom: '20px' }}>
          <Grid container direction="column" spacing={3}>
            <Grid item>
              <Typography display="block" variant="subtitle2" style={{ fontSize: '20px', fontWeight: 800 }}>
                Policy API Configuration
              </Typography>
            </Grid>

            {/* Simple One-Line Status Display */}
            <Grid item>
              <Grid container alignItems="center" spacing={2} style={{ padding: '8px 0' }}>
                <Grid item>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    Current Mode:
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography
                    variant="body2"
                    style={{
                      fontWeight: 500,
                      color: isMockIntegrationEnabled ? '#f57c00' : '#388e3c',
                    }}
                  >
                    {isMockIntegrationEnabled ? '🎭 Demo Mode (Mock Server)' : 'Production Policy Server'}
                    {isMockIntegrationEnabled ? ' ⚠️' : ' ✅'}
                  </Typography>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    color={isMockIntegrationEnabled ? 'primary' : 'secondary'}
                    onClick={() => handleOpenToggleDialog(!isMockIntegrationEnabled)}
                    disabled={isProcessing}
                    size="small"
                  >
                    {isProcessing
                      ? 'Switching...'
                      : isMockIntegrationEnabled
                        ? 'Switch to Production Mode'
                        : 'Switch to Demo Mode'}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      {/* Main Content Area */}
      <Grid item style={{ display: 'inline-flex', alignItems: 'center', paddingTop: '37px', paddingLeft: '32px' }}>
        {/* Enable buttons for when Policy API is not configured */}
        {(showEnablePolicyAPIButton || showEnableMockButton) && (
          <Grid>
            <Typography
              display="block"
              variant="subtitle2"
              style={{ fontSize: '20px', fontWeight: 800, marginBottom: '35px' }}
            >
              Policy API
            </Typography>
            <Grid container spacing={2}>
              {showEnablePolicyAPIButton && (
                <Grid item>
                  <Button
                    color="primary"
                    variant="contained"
                    style={{ paddingTop: '20px ', paddingBottom: '20px' }}
                    onClick={handleEnablePolicyAPI}
                    disabled={isProcessing}
                  >
                    Enable Policy API
                  </Button>
                </Grid>
              )}
              {showEnableMockButton && (
                <Grid item>
                  <Button
                    color="secondary"
                    variant="contained"
                    style={{ paddingTop: '20px ', paddingBottom: '20px' }}
                    onClick={handleEnableMockIntegration}
                    disabled={isProcessing}
                  >
                    Enable Mock Policy Integration
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        )}

        {/* Mock interface when enabled */}
        {showMockInterface && <PolicyManagementInterface organizationId={organization.id} />}

        {/* Regular Policy API Configuration */}
        {showRegularInterface && (
          <Grid item>
            {/* The per-organization carrier document: the two v2 endpoints with this
                organization's own configured search fields as real parameters or body properties.
                Not gated on version 2 -- this is the artifact handed to a carrier while onboarding
                them, which is before the configuration is flipped to v2. */}
            <Button
              color="primary"
              onClick={() => window.open(`${urlPolicyAPIConfig}/openapi.json`, '_blank')}
              startIcon={<DownloadIcon iconColor={colors.buttonLink} size={12} />}
            >
              Download Server-Side Spec
            </Button>
            <PolicyAPIDataForm
              title="Policy API Configurations"
              firstTitle="Base URL"
              secondTitle="Auth Token"
              thirdTitle="Policy Search Component Description"
              switchTitle="Enabled"
              wizardOpen={false}
              getEntityInitialValues={getEntityInitialValues}
              getValidationSchema={getValidationSchema}
              getEditInputs={getEditInputs}
              onSave={handleSave}
              openEdit={() => setPolicyAPIDataFormEditOpen(true)}
              onCancel={() => setPolicyAPIDataFormOpen(false)}
            />
          </Grid>
        )}
      </Grid>
      {policyAPIDataFormWizardOpen && (
        <PolicyAPIDataForm
          title="New Policy API Configuration"
          firstTitle="Base URL"
          secondTitle="Auth Token"
          thirdTitle="Policy Search Component Description"
          switchTitle="Enabled"
          wizardOpen={policyAPIDataFormWizardOpen}
          getEntityInitialValues={getEntityInitialValues}
          getValidationSchema={getValidationSchema}
          getEditInputs={getEditInputs}
          onSave={handleSave}
          onCancel={() => setPolicyAPIDataFormOpen(false)}
        />
      )}
      {policyAPIDataFormEditWizardOpen && (
        <PolicyAPIDataForm
          title="Set A New Policy API Configuration"
          firstTitle="Base URL"
          secondTitle="Auth Token"
          thirdTitle="Policy Search Component Description"
          switchTitle="Enabled"
          wizardOpen={policyAPIDataFormEditWizardOpen}
          getEntityInitialValues={getEntityInitialValues}
          getValidationSchema={getValidationSchema}
          getEditInputs={getEditInputs}
          onSave={handleEditSave}
          onCancel={() => setPolicyAPIDataFormEditOpen(false)}
        />
      )}

      {/* Mode Toggle Dialog */}
      <PolicyModeToggleDialog
        open={toggleDialog.open}
        onClose={handleCloseToggleDialog}
        onConfirm={handleConfirmToggle}
        isTogglingToDemo={toggleDialog.isTogglingToDemo}
        loading={isProcessing}
      />
    </Grid>
  );
};

export default PolicyAPIConfig;
